import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Typography,
  Box,
  TextField,
} from "@mui/material";

// Keep in sync with MaxDuplicateSampleCopies in LabOrderService.
const MAX_COPIES = 50;

const SAMPLE_INFO_FIELDS = [
  { key: "dateReceived", label: "Date Received" },
  { key: "originalSampleDate", label: "Original Sample Date" },
  { key: "sampleType", label: "Sample Type" },
  { key: "county", label: "County" },
  { key: "sampleMatrix", label: "Sample Matrix" },
  { key: "labNumber", label: "Lab Number" },
  { key: "dateCollected", label: "Date Sampled / Collected" },
  { key: "sampledBy", label: "Sampled By" },
  { key: "priority", label: "Priority" },
  { key: "validationProcess", label: "Validation Process" },
  { key: "rush", label: "Rush Sample" },
  { key: "complianceSample", label: "Compliance Sample" },
  { key: "sampleClassification", label: "Sample Classification" },
  { key: "facilityId", label: "Facility Id" },
  { key: "samplingPoint", label: "Sampling Point" },
  { key: "lab", label: "Lab" },
  { key: "sampleLocation", label: "Sample Location" },
  { key: "description", label: "Description" },
  { key: "comments", label: "Comments" },
];

const buildSampleInfoState = (checked) =>
  SAMPLE_INFO_FIELDS.reduce((acc, field) => {
    acc[field.key] = checked;
    return acc;
  }, {});

/**
 * Dialog shown when the user clicks "Duplicate" on a sample.
 * Lets them choose, individually, which pieces of the sample to copy:
 * - Assigned Tests
 * - Lab ID
 * - Sample Info (broken into individual fields, with its own Select All)
 * plus a global Select All across everything.
 *
 * They also choose how many copies to create, so several samples can be added
 * to an order in one pass.
 *
 * onConfirm receives:
 * {
 *   duplicateAssignedTests: boolean,
 *   duplicateLabId: boolean,
 *   sampleInfoFields: string[], // keys of the selected Sample Info fields
 *   copies: number
 * }
 */
const DuplicateSampleDialog = ({ open, onClose, onConfirm, sample }) => {
  const [duplicateAssignedTests, setDuplicateAssignedTests] = useState(true);
  const [duplicateLabId, setDuplicateLabId] = useState(false);
  const [sampleInfoFields, setSampleInfoFields] = useState(
    buildSampleInfoState(true)
  );
  // Held as a string so the field can be cleared while typing.
  const [copies, setCopies] = useState("1");

  // Reset selections every time the dialog opens for a (possibly new) sample
  useEffect(() => {
    if (open) {
      setDuplicateAssignedTests(true);
      setDuplicateLabId(false);
      setSampleInfoFields(buildSampleInfoState(true));
      setCopies("1");
    }
  }, [open, sample]);

  const parsedCopies = Number.parseInt(copies, 10);
  const copiesValid =
    Number.isInteger(parsedCopies) &&
    parsedCopies >= 1 &&
    parsedCopies <= MAX_COPIES;

  const sampleInfoValues = Object.values(sampleInfoFields);
  const allSampleInfoChecked = sampleInfoValues.every(Boolean);
  const someSampleInfoChecked = sampleInfoValues.some(Boolean);

  const allChecked =
    allSampleInfoChecked && duplicateAssignedTests && duplicateLabId;
  const someChecked =
    someSampleInfoChecked || duplicateAssignedTests || duplicateLabId;

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSampleInfoFields(buildSampleInfoState(checked));
    setDuplicateAssignedTests(checked);
    setDuplicateLabId(checked);
  };

  const handleSampleInfoSelectAll = (event) => {
    setSampleInfoFields(buildSampleInfoState(event.target.checked));
  };

  const handleFieldToggle = (key) => (event) => {
    setSampleInfoFields((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  const handleConfirm = () => {
    const selectedSampleInfoFields = Object.entries(sampleInfoFields)
      .filter(([, checked]) => checked)
      .map(([key]) => key);

    onConfirm({
      duplicateAssignedTests,
      duplicateLabId,
      sampleInfoFields: selectedSampleInfoFields,
      copies: parsedCopies,
    });
  };

  const nothingSelected =
    !duplicateAssignedTests && !duplicateLabId && !someSampleInfoChecked;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Sample</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose what to carry over to the new sample
          {sample?.sampleNumber
            ? ` (copy of Sample #${sample.sampleNumber})`
            : ""}
          .
        </Typography>

        <TextField
          label="Number of Copies"
          type="number"
          size="small"
          value={copies}
          onChange={(e) => setCopies(e.target.value)}
          error={!copiesValid}
          helperText={
            copiesValid
              ? `Creates ${parsedCopies} new sample${
                  parsedCopies === 1 ? "" : "s"
                } on this order.`
              : `Enter a whole number between 1 and ${MAX_COPIES}.`
          }
          inputProps={{ min: 1, max: MAX_COPIES, step: 1 }}
          sx={{ mb: 1, width: 200 }}
        />

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={!allChecked && someChecked}
              onChange={handleSelectAll}
            />
          }
          label={<strong>Select All</strong>}
        />

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={duplicateAssignedTests}
              onChange={(e) => setDuplicateAssignedTests(e.target.checked)}
            />
          }
          label="Assigned Tests"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={duplicateLabId}
              onChange={(e) => setDuplicateLabId(e.target.checked)}
            />
          }
          label="Lab ID"
        />

        <Divider sx={{ my: 1.5 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={allSampleInfoChecked}
              indeterminate={!allSampleInfoChecked && someSampleInfoChecked}
              onChange={handleSampleInfoSelectAll}
            />
          }
          label={<strong>Sample Info</strong>}
        />

        <Box sx={{ pl: 4 }}>
          <FormGroup>
            {SAMPLE_INFO_FIELDS.map((field) => (
              <FormControlLabel
                key={field.key}
                control={
                  <Checkbox
                    size="small"
                    checked={sampleInfoFields[field.key]}
                    onChange={handleFieldToggle(field.key)}
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={nothingSelected || !copiesValid}
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateSampleDialog;